import i18n from 'i18n';
import { converters as tableConverters } from 'store/table';
import { sum } from 'utils/general';
import { IBaseTransmissionAct, ITransmissionAct } from 'types/income';
import { loaders } from './transmission-acts.loaders';
import {
  Transaction,
  BaseTransmissionAct,
  TransmissionAct,
} from './transmission-acts.types';

export const convertBaseTransmissionAct = (
  transmissionAct: IBaseTransmissionAct,
): BaseTransmissionAct => {
  return {
    id: transmissionAct.id,
    company: transmissionAct.companyId
      ? {
          id: transmissionAct.companyId,
          name: transmissionAct.companyName ?? '',
          status: transmissionAct.companyStatus,
        }
      : null,
    billingInfo: transmissionAct.billingInfo ?? '',
    from: transmissionAct.from,
    to: transmissionAct.to ?? null,
    openingBalance: transmissionAct.openingBalance,
    closingBalance: transmissionAct.closingBalance,
  };
};

export const convertTransmissionAct = (
  transmissionAct: ITransmissionAct,
): TransmissionAct => {
  return {
    ...convertBaseTransmissionAct(transmissionAct),
    balance: {
      deposits: {
        transactions: transmissionAct.rows.deposits.map((deposit) => ({
          title: deposit.title,
          amount: deposit.amount,
          currency: deposit.currency,
          date: deposit.date,
        })),
        total: sum(
          ...transmissionAct.rows.deposits.map((deposit) => deposit.amount),
        ),
      },
      expenses: {
        transactions: transmissionAct.rows.expenses.map((expense) => ({
          title: expense.title,
          amount: expense.subtotal,
          currency: expense.currency,
          count: expense.count,
        })),
        total: sum(
          ...transmissionAct.rows.expenses.map((expense) => expense.subtotal),
        ),
      },
    },
  };
};

const convertTransmissionActs = (
  transmissionActs: IBaseTransmissionAct[],
): BaseTransmissionAct[] => {
  return transmissionActs.map(convertBaseTransmissionAct);
};

const convertTransactions = (
  transmissionAct: TransmissionAct,
): Transaction[] => {
  let balance: number = transmissionAct.openingBalance;

  return [
    ...transmissionAct.balance.expenses.transactions.map((expense, i) => {
      balance -= expense.amount;

      return {
        type: 'expense' as const,
        id: `expense-${i}`,
        title:
          expense.title === 'reimbursementClaim'
            ? i18n.t('claims.title')
            : expense.title === 'marketplaceOrder'
            ? i18n.t('menu.orders')
            : '',
        amount: expense.amount,
        currency: expense.currency,
        count: expense.count,
        balance,
      };
    }),
    ...transmissionAct.balance.deposits.transactions.map((deposit, i) => {
      balance += deposit.amount;
      return {
        type: 'deposit' as const,
        id: `deposit-${i}`,
        title: deposit.title,
        amount: deposit.amount,
        currency: deposit.currency,
        date: deposit.date,
        balance,
      };
    }),
  ];
};

export const converters = {
  getTransmissionActs: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getTransmissionActs>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertTransmissionActs(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getTransmissionAct: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getTransmissionAct>>) => {
    return convertTransmissionAct(data);
  },
  getTransactions: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getTransactions>>) => {
    const filtered = data.data[0];

    if (!filtered)
      return {
        items: [],
        total: 0,
        meta: {
          openingBalance: 0,
          closingBalance: 0,
        },
      };

    const converted = convertTransactions(convertTransmissionAct(filtered));

    return {
      items: converted,
      total: converted.length,
      meta: {
        openingBalance: filtered.openingBalance,
        closingBalance: filtered.closingBalance,
      },
    };
  },
};
